//
// Compliant analytics dispatcher.
//
// Source spec: Website Build Handoff V1.0, sec. 8 "SEO, Tracking and Analytics
// Requirements" + sec. 9 "Compliance Guardrails".
//
// Two hard rules baked into this file:
//   1. NEVER include health PII (BMI, weights, diagnoses, medication names,
//      symptoms) in event params. Categorical buckets only.
//   2. Event names and parameter keys match the spec table verbatim.
//
// Implementation: pushes to `window.dataLayer` (GTM-compatible) and also
// invokes `window.gtag` when present (direct GA4). If neither is set up,
// events are queued silently so we don't break anything pre-launch.
//

const ALLOWED_EVENTS = new Set([
  'page_view',
  'cta_click',
  'assessment_start',
  'consent_accept',
  'assessment_complete',
  'result_view',
  'whatsapp_click',
  'consultation_booked',
  'payment_success',
  'follow_up_click',
  // GA4 conversion events per MyDrAI_SEO_Master.doc §6.
  'assessment_started',
  'otp_verified',
  'begin_checkout',
  'purchase',
  'consultation_completed_patient',
  'waitlist_submitted',
]);

// Parameter keys that may contain ANY health detail. If a caller accidentally
// passes one of these we drop it before dispatch — defensive guardrail so a
// future analytics call can't leak PII.
const BLOCKED_PARAM_KEYS = new Set([
  'bmi', 'weight', 'weight_kg', 'height', 'height_cm', 'age',
  'diagnosis', 'medication', 'medications', 'allergy', 'symptom', 'symptoms',
  'pregnancy', 'condition', 'red_flag', 'red_flags', 'contraindication',
  'name', 'email', 'phone', 'mobile', 'nric',
]);

function sanitize(params) {
  if (!params || typeof params !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(params)) {
    if (BLOCKED_PARAM_KEYS.has(k.toLowerCase())) continue;
    // Coerce primitives only — no nested objects (which could smuggle PII).
    if (v == null) continue;
    const t = typeof v;
    if (t === 'string' || t === 'number' || t === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}

export function track(eventName, params = {}) {
  if (!ALLOWED_EVENTS.has(eventName)) {
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(`[analytics] event "${eventName}" is not in the approved list — skipped.`);
    }
    return;
  }
  if (typeof window === 'undefined') return;

  const payload = { event: eventName, ...sanitize(params) };

  // GTM (preferred when client adds a container).
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);

  // GA4 direct (works without GTM).
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, sanitize(params));
  }
}

// Convenience wrappers — keeps call sites readable and consistent.
export function trackCtaClick({ page, button_label, destination }) {
  track('cta_click', { page, button_label, destination });
}
export function trackAssessmentStart({ source_page, language }) {
  track('assessment_start', { source_page, language });
}
export function trackConsentAccept({ consent_version }) {
  track('consent_accept', {
    consent_version,
    timestamp: new Date().toISOString(),
  });
}
export function trackAssessmentComplete({ source_page, result_category }) {
  // result_category is a categorical bucket, NOT raw health data.
  track('assessment_complete', { source_page, result_category });
}
export function trackResultView({ result_category }) {
  track('result_view', { result_category });
}
export function trackWhatsAppClick({ source_page, cta_label }) {
  track('whatsapp_click', { source_page, CTA_label: cta_label });
}
export function trackConsultationBooked({ consultation_type } = {}) {
  track('consultation_booked', { consultation_type });
}

// ─── GA4 conversion event helpers (SEO Master §6) ───────────────────────────
// Named exactly as the client spec requires so GTM triggers map 1:1.

export function trackAssessmentStarted({ source_page, language } = {}) {
  track('assessment_started', { source_page, language });
}
export function trackOtpVerified({ purpose } = {}) {
  // purpose is a categorical bucket ('signup' | 'reset' | 'login'); no PII.
  track('otp_verified', { purpose });
}
export function trackBeginCheckout({ value = 1599.00, currency = 'MYR', package_code } = {}) {
  track('begin_checkout', { value, currency, package_code });
}
export function trackPurchase({ transaction_id, value = 1599.00, currency = 'MYR', package_code } = {}) {
  track('purchase', { transaction_id, value, currency, package_code });
}
export function trackConsultationCompletedPatient({ booking_id } = {}) {
  track('consultation_completed_patient', { booking_id });
}
export function trackWaitlistSubmitted({ source_page, list = 'preventive_health' } = {}) {
  track('waitlist_submitted', { source_page, list });
}
